import type { ReactNode } from 'react'

import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { STATUS_SUCCESS_TOKENS } from './statusTokens'

interface StatusPillProps {
  /** Pill label. */
  label: ReactNode
  /** Optional leading icon (e.g. a check-circle). */
  icon?: ReactNode
  /** Pill background color token. */
  bgcolor?: string
  /** Pill border color token. */
  borderColor?: string
  /** Label color token. */
  labelColor?: string
  sx?: SxProps
}

/**
 * A small rounded status pill: optional icon + label on a tinted, bordered
 * surface.
 *
 * Figma: the "Categorized" pill in Peer-to-Peer Categorization (`success.100`
 * bg, `success.main` border, radius 999, 24px tall, check icon + `success.darker`
 * `typography/subtitle2` label — regular weight, NOT bold). Reusable for any
 * inline status.
 */
const StatusPill = ({
  bgcolor = STATUS_SUCCESS_TOKENS.bgcolor,
  borderColor = STATUS_SUCCESS_TOKENS.borderColor,
  icon,
  label,
  labelColor = STATUS_SUCCESS_TOKENS.textColor,
  sx,
}: StatusPillProps) => (
  <Stack
    sx={{
      alignItems: 'center',
      alignSelf: 'flex-start',
      bgcolor,
      border: '1px solid',
      borderColor,
      borderRadius: 999,
      flexDirection: 'row',
      gap: 6,
      minHeight: 24,
      px: 16,
      py: 2,
      ...sx,
    }}
  >
    {icon}
    <Text color={labelColor} variant="subtitle2">
      {label}
    </Text>
  </Stack>
)

export default StatusPill
